import {
  Boxes,
  Calculator,
  Cable,
  ClipboardList,
  DownloadCloud,
  LayoutDashboard,
  PackageSearch,
  RefreshCw,
  Search,
  Settings,
  Store,
  Warehouse,
  Bell,
  Languages,
  LogOut
} from "lucide-react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { clearToken } from "../lib/api";
import { useI18n } from "../lib/i18n";

const navigation = [
  { to: "/", label: "概览", icon: LayoutDashboard },
  { to: "/stores", label: "店铺", icon: Store },
  { to: "/products", label: "商品", icon: PackageSearch },
  { to: "/collection", label: "采集箱", icon: DownloadCloud },
  { to: "/pricing-templates", label: "定价模板", icon: Calculator },
  { to: "/orders", label: "订单", icon: ClipboardList },
  { to: "/inventory", label: "库存", icon: Warehouse },
  { to: "/connectors", label: "连接器", icon: Cable },
  { to: "/sync-jobs", label: "同步任务", icon: RefreshCw },
  { to: "/settings", label: "设置", icon: Settings }
];

const pageTitles: Record<string, string> = {
  "/": "运营概览",
  "/stores": "店铺管理",
  "/products": "商品与 SKU",
  "/collection": "产品采集",
  "/pricing-templates": "定价模板",
  "/orders": "订单中心",
  "/inventory": "库存中心",
  "/connectors": "平台连接器",
  "/sync-jobs": "同步任务",
  "/settings": "系统设置"
};

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage, t } = useI18n();
  const user = JSON.parse(localStorage.getItem("erp_user") || "null") as { name?: string; role?: string } | null;
  const pageTitle = location.pathname.startsWith("/products/")
    ? "商品编辑"
    : pageTitles[location.pathname] ?? "运营工作台";

  function logout() {
    clearToken();
    localStorage.removeItem("erp_user");
    navigate("/login");
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-block">
          <div className="brand-mark">
            <Boxes size={21} />
          </div>
          <div>
            <strong>{t("跨境电商 ERP")}</strong>
            <span>{t("全渠道运营工作台")}</span>
          </div>
        </div>
        <nav className="side-nav">
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} end={item.to === "/"} className={({ isActive }) => (isActive ? "nav-item active" : "nav-item")}>
                <Icon size={18} />
                <span>{t(item.label)}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <span>{t("运行环境")}</span>
          <strong>{t("本地 PGlite")}</strong>
          <small>{t("生产切换 PostgreSQL")}</small>
        </div>
      </aside>

      <main className="main-area">
        <header className="topbar">
          <div className="workspace-switcher">
            <span className="workspace-dot" />
            <strong>{t("全球业务部")}</strong>
          </div>
          <label className="global-search">
            <Search size={17} />
            <input placeholder={t("搜索订单、产品、店铺、SKU...")} />
          </label>
          <div className="topbar-actions">
            <span className="date-chip">2026-09-17 至 2026-09-23</span>
            <button type="button" className="ghost-button">
              <RefreshCw size={16} />
              {t("刷新")}
            </button>
            <button type="button" className="icon-button" aria-label={t("通知")}>
              <Bell size={18} />
            </button>
            <button
              type="button"
              className="ghost-button language-button"
              onClick={() => setLanguage(language === "zh" ? "en" : "zh")}
              aria-label="Switch language"
            >
              <Languages size={16} />
              {language === "zh" ? "EN" : "中文"}
            </button>
            <div className="user-menu">
              <div className="avatar">{user?.name?.slice(0, 1) ?? "管"}</div>
              <div>
                <strong>{user?.name === "系统管理员" ? t("系统管理员") : user?.name ?? t("系统管理员")}</strong>
                <span>{user?.role ?? "admin"}</span>
              </div>
              <button type="button" className="icon-button" onClick={logout} aria-label={t("退出登录")}>
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </header>
        <div className="page-heading">
          <div>
            <h1>{t(pageTitle)}</h1>
            <p>{t("统一管理 Amazon、TikTok Shop 与 SHEIN 的订单、商品、库存和同步任务。")}</p>
          </div>
        </div>
        <div className="page-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
